import React, { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import { FiCalendar, FiCheckCircle, FiSlash } from "react-icons/fi";
import { useAuth } from "../../provider/auth.provider";
import { useConfirm } from "../../provider/confirm.provider";
import {
  getManualMarkSettings,
  updateManualMarkSettings,
  type ManualMarkSettings,
} from "../../libs/attendance";

/**
 * Masters > Manual Attendance.
 *
 * One switch: may TODAY's date be manually marked. It is on by default. Past
 * dates are always markable and future dates never are — neither is a setting,
 * so both are stated here as rules rather than offered as controls.
 */

const formatDay = (date?: string) => {
  if (!date) return "-";
  const d = new Date(`${date}T00:00:00`);
  return isNaN(d.getTime())
    ? date
    : d.toLocaleDateString(undefined, { weekday: "short", day: "2-digit", month: "short", year: "numeric" });
};

const formatMoment = (value?: string | null) => {
  if (!value) return "";
  const d = new Date(value);
  return isNaN(d.getTime())
    ? ""
    : d.toLocaleString(undefined, { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

const ManualMarkSettingsPage: React.FC = () => {
  const { user, hasPermission } = useAuth();
  const { confirm } = useConfirm();
  const canManage = user?.role?.level === 1 || hasPermission("attendance", "manage_manual_mark_settings");

  const [settings, setSettings] = useState<ManualMarkSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setSettings(await getManualMarkSettings());
    } catch {
      toast.error("Failed to load manual attendance settings");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const toggle = async () => {
    if (!settings || !canManage) return;
    const next = !settings.allow_today;
    const ok = await confirm({
      title: next ? "Allow manual attendance for today?" : "Disable manual attendance for today?",
      message: next
        ? "Everyone who can use Manual Mark will be able to mark today's date again."
        : "Nobody will be able to manually mark today's date — from the Manual Mark button, the calendar or a Not Marked cell — until this is turned back on. Earlier dates stay open.",
      confirmText: next ? "Allow today" : "Disable today",
      variant: next ? "info" : "warning",
    });
    if (!ok) return;
    setSaving(true);
    try {
      const updated = await updateManualMarkSettings(next);
      setSettings(updated);
      toast.success(next ? "Manual attendance for today is allowed" : "Manual attendance for today is disabled");
    } catch (error: any) {
      toast.error(error?.response?.data?.message || "Failed to update the setting");
    } finally {
      setSaving(false);
    }
  };

  const allowToday = settings?.allow_today !== false;
  const changedBy = settings?.updated_by
    ? `${settings.updated_by.first_name || ""} ${settings.updated_by.last_name || ""}`.trim()
    : "";

  return (
    <div className="space-y-4">
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="flex items-center gap-3 px-4 py-4 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-white">
          <div className="w-10 h-10 rounded-lg bg-blue-800 text-white flex items-center justify-center flex-shrink-0">
            <FiCalendar className="w-5 h-5" />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-gray-900">Manual Attendance</h1>
            <p className="text-sm text-gray-600">Which dates can be marked through Manual Mark.</p>
          </div>
        </div>

        {loading ? (
          <div className="px-4 py-10 text-center text-sm text-gray-600">Loading...</div>
        ) : !settings ? (
          <div className="px-4 py-10 text-center text-sm text-gray-600">
            The setting could not be loaded.{" "}
            <button onClick={load} className="text-blue-700 hover:underline">
              Try again
            </button>
          </div>
        ) : (
          <div className="p-4 space-y-4">
            <div
              className={`flex flex-col md:flex-row md:items-center md:justify-between gap-4 rounded-lg border px-4 py-4 ${
                allowToday ? "border-green-200 bg-green-50" : "border-amber-200 bg-amber-50"
              }`}
            >
              <div className="flex items-start gap-3">
                {allowToday ? (
                  <FiCheckCircle className="w-5 h-5 mt-0.5 text-green-700 flex-shrink-0" />
                ) : (
                  <FiSlash className="w-5 h-5 mt-0.5 text-amber-700 flex-shrink-0" />
                )}
                <div>
                  <div className="text-sm font-semibold text-gray-900">
                    Manual attendance for today's date is {allowToday ? "allowed" : "disabled"}
                  </div>
                  <p className="text-sm text-gray-700 mt-0.5">
                    {allowToday
                      ? `Today (${formatDay(settings.today)}) can be marked from the Manual Mark button, the calendar and a Not Marked cell in the matrix.`
                      : `Today (${formatDay(settings.today)}) cannot be manually marked by anyone. The latest date that can be marked is ${formatDay(settings.max_date)}.`}
                  </p>
                  {settings.updated_at && (
                    <p className="text-xs text-gray-500 mt-1">
                      Last changed {formatMoment(settings.updated_at)}
                      {changedBy ? ` by ${changedBy}` : ""}
                    </p>
                  )}
                </div>
              </div>

              {canManage ? (
                <button
                  type="button"
                  role="switch"
                  aria-checked={allowToday}
                  aria-label="Allow manual attendance for today's date"
                  onClick={toggle}
                  disabled={saving}
                  className={`relative inline-flex h-7 w-14 flex-shrink-0 items-center rounded-full transition-colors disabled:opacity-60 ${
                    allowToday ? "bg-green-600" : "bg-gray-300"
                  }`}
                >
                  <span
                    className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
                      allowToday ? "translate-x-8" : "translate-x-1"
                    }`}
                  />
                </button>
              ) : (
                <span className="text-xs text-gray-500">You do not have permission to change this.</span>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
              <div className="border border-gray-200 rounded-lg p-3">
                <div className="text-xs font-semibold uppercase tracking-wide text-gray-400">Earlier dates</div>
                <p className="text-gray-700 mt-1">Always allowed. This switch does not affect them.</p>
              </div>
              <div className="border border-gray-200 rounded-lg p-3">
                <div className="text-xs font-semibold uppercase tracking-wide text-gray-400">Today</div>
                <p className="text-gray-700 mt-1">Allowed by default. Controlled by the switch above, for every user.</p>
              </div>
              <div className="border border-gray-200 rounded-lg p-3">
                <div className="text-xs font-semibold uppercase tracking-wide text-gray-400">Future dates</div>
                <p className="text-gray-700 mt-1">Never allowed, whatever the switch says.</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ManualMarkSettingsPage;
